import Editor, { useMonaco } from '@monaco-editor/react'
import { connect, FieldArray, FormikValues } from 'formik'
import { flatten, get } from 'lodash'
import { useEffect, useRef } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Divider, Icon, Popup } from 'semantic-ui-react'
import styled from 'styled-components'
import { v4 } from 'uuid'
import { array, object, string } from 'yup'

import Button from '@/components/Button'
import { Text } from '@/components/Form'
import { Beautify, Delete } from '@/components/Icons'
import { FormLabel, Row } from '@/components/StyledComponents'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import { CustomCodeStepData, StepData, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'
import MarkdownParser from '@/utilities/markdownProcessors/MarkdownProcessor'

import messages from './messages'
import { AddButton } from './Notification/ButtonBlock'

//<editor-fold desc="Styled Components">
const EventAttributeRow = styled<any>(Row)`
  && {
    flex: 1;
    width: 100%;
    align-items: flex-start;
    margin-top: 0px;
    margin-bottom: 0px;
    > div {
      flex: 1 0 30%;
    }

    .form__field {
      margin-top: 0;
      margin-left: 10px;
      > div > div {
        padding: 0.6rem;
      }
    }

    .field__input {
      padding: 0.5rem;
    }
  }
`
const StyledFormLabel = styled(FormLabel)`
  margin-bottom: 10px;
`

const StyledFormDescription = styled.p`
  margin-top: 5px;
  margin-bottom: 15px;
`

const ScriptOperationContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledBeautifyIcon = styled(Beautify)`
  margin-left: 5px;
`

const StyledDeleteIconButton = styled.button`
  background: transparent;
  outline: none;
  border: none;
  margin-top: auto;
  margin-bottom: auto;
  margin-left: 10px;
  margin-right: 10px;
  cursor: pointer;
`

const StyledButton = styled(Button)`
  padding: 0 0.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  margin-left: auto;
  min-height: 20px;
`

const StyledBareButton = styled.button`
  outline: none;
  background: none;
  cursor: pointer;
  border: none;
`
const StyledVariableAutoComplete = styled(VariableAutoComplete)`
  width: 300px;
  max-width: 300px;

  &.form__filter-select .field__wrapper .ui.dropdown.fluid .menu {
    max-width: 350px;
  }
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type ScriptProps = {
  readOnly?: boolean
}
//</editor-fold>

const getValidationSchema = (intl: IntlShape) => {
  return {
    parameters: array().of(
      object().shape({
        key: string().required(intl.formatMessage(messages.scriptParameterNameRequired)),
        value: string().required(intl.formatMessage(messages.scriptParameterValueRequired))
      })
    )
  }
}

const getInitialValues = (stepData: CustomCodeStepData): StepData => ({
  parameters: stepData.parameters,
  body: get(
    stepData,
    'body',
    `
//data to be returned from the script step
done({})`
  )
})
const getCustomCodeOperationHelpUrl = 'https://help.workgrid.com/s/article/Utilizing-the-Custom-Code-Operation'

const CustomCodeOperation = ({ formik, readOnly = false }: ScriptProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()
  const flattendVariables = getAllFlattenedVariables(flatten(variables))
  const monacoRef = useRef<any>(null)
  const monaco = useMonaco()

  useEffect(() => {
    if (monaco) {
      // extra libraries
      const libSource = `
      interface InputParams {
      ${formik.values.stepData.parameters?.map((param: any) => {
        const variable = flattendVariables.find(variable => variable.name === param.value)
        let tsType: string | undefined
        switch (variable?.type) {
          case 'array': {
            tsType = 'Array<Any>'
            break
          }
          case 'object': {
            tsType = 'any'
            break
          }
          default: {
            tsType = variable?.type
            break
          }
        }

        return `/** example: ${variable?.example ?? 'n/a'} */ ${param.key}: ${tsType || 'any'};`
      })}
      };

      interface Packages {
        /** Loads external module (e.g. "lodash") */  lodash: Module;
        /** Loads external module (e.g. "moment") */  moment: Module;
        /** Loads external module (e.g. "crypto") */  crypto: Module;
      }

      // parameters
      /** Loads external module (e.g. "lodash") */  declare var packages: Packages;
      declare var input: InputParams;
      /** custom code callback */ declare var done: (response: any)=>{};`

      const libUri = `ts:filename/codeEditor.d.ts`
      monaco.languages.typescript.javascriptDefaults.addExtraLib(libSource, libUri)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [monaco, formik.values.stepData.parameters])

  useEffect(() => {
    if (monaco) {
      monaco.languages.typescript.javascriptDefaults.addExtraLib(
        '/** Loads external module (e.g. "lodash") */ var require = function(moduleName) {return new Module();};',
        'js:require.js'
      )

      // validation settings
      monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: false,
        noSyntaxValidation: false
      })

      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES2017,
        allowNonTsExtensions: true,
        lib: ['!DOM', 'es2015'],
        moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
        module: monaco.languages.typescript.ModuleKind.ESNext,
        typeRoots: ['node_modules/@types'],
        alwaysStrict: true,
        noUnusedParameters: true,
        noImplicitUseStrict: true,
        noUnusedLocals: true
      })
    }
  }, [monaco])

  const onBeautify = () => {
    monacoRef.current.getAction('editor.action.formatDocument').run()
  }

  const onSelectVariable = (variable: Variable, formikName: string) => {
    formik.setFieldValue(formikName, variable.name)
  }

  const mounted = (editor: any) => {
    monacoRef.current = editor
  }

  const handleEditorChange = (value: any) => {
    formik.setFieldValue(`stepData.body`, value)
  }

  return (
    <ScriptOperationContainer>
      {variables.length > 0 && (
        <FieldArray
          name="stepData.parameters"
          render={arrayHelpers => (
            <>
              <StyledFormLabel>{intl.formatMessage(messages.codeEditorDescription)}</StyledFormLabel>
              {formik.values.stepData.parameters?.map((param: any, i: number) => (
                <div key={param.id}>
                  <EventAttributeRow>
                    <Text
                      disabled={readOnly}
                      name={`stepData.parameters[${i}].key`}
                      placeholder={intl.formatMessage(messages.codeEditorKeyPlaceholder)}
                    />
                    <StyledVariableAutoComplete
                      readOnly={readOnly}
                      placeholder={intl.formatMessage(messages.codeEditorValuePlaceholder)}
                      name={`stepData.parameters[${i}].value`}
                      variables={variables}
                      onSelectOption={onSelectVariable}
                    />
                    {!readOnly && (
                      <StyledDeleteIconButton onClick={() => arrayHelpers.remove(i)}>
                        <Delete title="Delete" />
                      </StyledDeleteIconButton>
                    )}
                  </EventAttributeRow>
                </div>
              ))}
              {!readOnly && (
                <AddButton
                  message="addVariableInput"
                  onClick={() =>
                    arrayHelpers.push({
                      id: v4(),
                      key: '',
                      value: ''
                    })
                  }
                />
              )}

              <Divider section />
            </>
          )}
        />
      )}
      <Row>
        <FormLabel>{intl.formatMessage(messages.customScript)}</FormLabel>
        <Popup
          on="click"
          content={<p>{MarkdownParser(intl.formatMessage(messages.codeEditorTooltip))}</p>}
          trigger={
            <StyledBareButton>
              <Icon name={'info circle'} />
            </StyledBareButton>
          }
        />
      </Row>
      <StyledFormDescription>
        {intl.formatMessage(variables.length > 0 ? messages.inputDescription : messages.codeEditorNoVariablesWarning, {
          variableName: get(formik.values, `stepData.parameters[0].key`, intl.formatMessage(messages.variableName))
        })}
      </StyledFormDescription>

      <div className="mt-2 h-[650px] max-h-[650px] rounded-md border px-3 pb-20 pt-3 shadow-md">
        <div className="mb-3 flex justify-between">
          <div className="italic text-gray-500">
            {intl.formatMessage(
              variables.length > 0
                ? messages.javascriptFunctionDeclaration
                : messages.javascriptFunctionDeclarationWithNoInput
            )}
            <span>&#123;</span>
          </div>
          <StyledButton color="transparent" onClick={onBeautify} size="small">
            {intl.formatMessage(messages.beautify)}
            <StyledBeautifyIcon />
          </StyledButton>
        </div>
        <Editor
          onMount={mounted}
          theme="light"
          onChange={handleEditorChange}
          options={{
            readOnly,
            automaticLayout: true,
            lineNumbersMinChars: 2,
            scrollBeyondLastLine: false,
            lineDecorationsWidth: 0,
            formatOnPaste: true,
            minimap: {
              enabled: false
            },
            scrollbar: {
              useShadows: false,
              alwaysConsumeMouseWheel: false
            }
          }}
          defaultLanguage="javascript"
          defaultValue={formik.values.stepData.body}
        />
        <div className="mt-1 italic text-gray-500">&#125;</div>
      </div>
    </ScriptOperationContainer>
  )
}

export default connect(CustomCodeOperation)
export { getValidationSchema, getInitialValues, getCustomCodeOperationHelpUrl }
